import { useRef } from 'react';
import { useInteractionHandler, SKIP_RECORDING } from '../../hooks/use-interaction-handler.js';
import { useCallbackIfFlagEnabled } from '../../hooks/use-memoization.js';
import { composeHooks } from '../../utils/compose-hooks.js';
import { useCommitOnBlur } from './use-commit-on-blur.js';
import { useFormatter } from './use-formatter.js';
import { useSelectOnClick } from './use-select-on-click.js';

const _useFormattedInput = composeHooks(useFormatter, useCommitOnBlur, useSelectOnClick);
function useFormattedInput({ value, formatter, onChange: onChangeIn, onInput, recordingKey, id, 'aria-label': ariaLabel, ...props }) {
    const recordedOnChange = useInteractionHandler((newValue, opts) => {
        if (props.disabled)
            return SKIP_RECORDING;
        onChangeIn(newValue, opts);
        // Skip recording `change` events because those are already recorded as `commit` events
        // by the `useCommitOnBlur` hook, and we don't want to record them twice. Also skip non-commit
        // events so that we only record the final value of any given interaction. Also skip `nudge`
        // events because those are recorded as `keydown` events in the `useFormatter` hook.
        if (!opts.commit || opts.source === 'change' || opts.source === 'nudge') {
            return SKIP_RECORDING;
        }
    }, { eventName: 'change', recordingKey }, [onChangeIn, props.disabled]);
    const inputRef = useRef(null);
    const onChange = useCallbackIfFlagEnabled((newValue, source) => recordedOnChange(newValue, { commit: true, source }), [recordedOnChange], 'uxp_memoization_optimizations');
    const hookProps = _useFormattedInput({
        ...props,
        ref: inputRef,
        value,
        formatter,
        onChange,
        onInput,
    }, { recordingKey });
    // A hack to satisfy typescript, the props rest syntax becomes
    // `Omit<Props, 'value' | 'formatter' etc>` which doesn't distribute the generic over
    // the id/label union type. So we have to cast it ourselves to satisfy the Input's types.
    const ariaProps = { id, 'aria-label': ariaLabel };
    const getStringValue = () => inputRef.current.value;
    // NOTE: not memoizing this context value because it's unlikely that nothing will change
    // between rerenders since we're passing all props from here to the input field inside.
    return {
        value,
        formatter,
        onChange: recordedOnChange,
        onChangeRestricted: props.onChangeRestricted,
        getStringValue,
        inputProps: { ...ariaProps, ...hookProps },
    };
}
const useScrubbableInput = useFormattedInput;

export { useFormattedInput, useScrubbableInput };
//# sourceMappingURL=use-formatted-input.js.map
